import { cliSimpleGit } from '../../cli-simple-git.js';
import { yieldToEventLoop } from '../../../runtime/yield-to-event-loop.js';
import { LINE_CHUNK_SIZE, type ReadFileResult } from '../../../files/read/types.js';
import type { FileReadEncoding } from '../../../files/read/index.js';
import { guessMimeType } from '../../../files/read/guess-mime-type.js';
import { gitBlobRef } from './git-blob-ref.js';
import { isBinaryBuffer } from './is-binary-buffer.js';
import {
  decodeGitBlobUtf8Async,
  readGitBlobLineRangeAsync,
  readGitBlobUtf8FullAsync,
} from './read-git-blob-text-async.js';

/**
 * Reads a file at a branch ref via `git show` (repo-relative path).
 */
export async function readGitTreeFileAsync(
  repoAbsPath: string,
  branch: string,
  relativePath: string,
  startLine?: number,
  endLine?: number,
  lineOffset?: number,
  lineChunkSize: number = LINE_CHUNK_SIZE,
  encoding: FileReadEncoding = 'utf8',
): Promise<ReadFileResult | { error: string }> {
  await yieldToEventLoop();
  const branchRef = branch.trim();
  if (!branchRef) return { error: 'branch required' };
  const rel = relativePath.replace(/^\/+/, '').replace(/\\/g, '/');
  if (!rel || rel === '.') return { error: 'file path required' };
  const blobRef = await gitBlobRef(repoAbsPath, branchRef, rel);
  if (!blobRef) return { error: 'Branch not found' };
  const hasRange = typeof startLine === 'number' && typeof endLine === 'number';
  try {
    if (encoding === 'base64') {
      if (hasRange) return { error: 'base64 encoding requires a full file read (no line range)' };
      const buf = await cliSimpleGit(repoAbsPath).showBuffer([blobRef]);
      await yieldToEventLoop();
      return {
        content: buf.toString('base64'),
        size: buf.length,
        mimeType: guessMimeType(rel),
      };
    }
    const buf = await cliSimpleGit(repoAbsPath).showBuffer([blobRef]);
    await yieldToEventLoop();
    const size = buf.length;
    if (isBinaryBuffer(buf)) {
      return { error: 'Binary file cannot be displayed as text' };
    }
    if (hasRange) {
      const ranged = await readGitBlobLineRangeAsync(buf, startLine, endLine, lineOffset, lineChunkSize);
      return {
        content: ranged.content,
        totalLines: ranged.totalLines,
        size,
        ...(ranged.lineOffset != null ? { lineOffset: ranged.lineOffset } : {}),
      };
    }
    const full = await readGitBlobUtf8FullAsync(buf);
    return { content: full.content, totalLines: full.totalLines, size, mimeType: guessMimeType(rel) };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (/does not exist|bad revision|exists on disk, but not in/i.test(message)) {
      return { error: 'File not found in branch' };
    }
    return { error: message };
  }
}
